import { useRef } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"
import { deleteTrip, duplicateTrip, updateTrip } from "store/trips"
import { notification } from "ui/Notification"
import { getActionTranslations } from "utils/translations"
import { loadingSelector } from "modules/loader-watchers/selectors"
import { useShareModal } from "./useShareModal"
import { useNavigate, useSearchParams } from "react-router-dom"

const { EXPERIENCE_STATES } = require("constants/experiences")

const NOTIFICATION_DURATION = {
  success: 3000,
  error: 5000
}

const triggerNotification = (type, message) => {
  notification[type]({
    message,
    type,
    closable: true,
    duration: NOTIFICATION_DURATION[type] || 3000
  })
}

export const useExperiencesActions = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { showConfirm,modal: confirmModal } = useNewConfirmModal()
  const { showShare, modal: shareModal } = useShareModal()
  const loading = useSelector(loadingSelector("trips.trip"))
  const translations = getActionTranslations(t)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isExecutingRef = useRef(false) //  Prevent duplicate actions
  const currentActionTypeRef = useRef(null)
  const currentTripIdRef = useRef(null)

  const executeAction = async (trip, actionType, action, onSuccess) => {
    if (isExecutingRef.current) return //Skip if already running
    isExecutingRef.current = true // Lock execution
    currentActionTypeRef.current = actionType
    currentTripIdRef.current = trip.id

    const { success, error } = translations[actionType]

    try {
      await dispatch(action(trip.id))
      triggerNotification("success", success)
      if (onSuccess) onSuccess()
    } catch (err) {
      console.error("executeAction error:", err)
      triggerNotification("error", error)
    } finally {
      isExecutingRef.current = false // Unlock
      currentActionTypeRef.current = null
      currentTripIdRef.current = null
    }
  }

  const handleExperiencesAction = (trip, actionType, action, onSuccess) => {
    if (isExecutingRef.current) return

    const {
      title,
      message,
      confirmLabel = t("global.confirm"),
      warningText
    } = translations[actionType]

    const isPreviewMode = searchParams.get("readOnlyPreview") === "true"
    const backRoute = searchParams.get("route")

    const afterAction = () => {
      if (onSuccess) onSuccess()
      if (isPreviewMode) {
        if (backRoute === EXPERIENCE_STATES.deactivated) {
          navigate("/pro/home/experiences?tab=deactivated")
        } else if (backRoute === EXPERIENCE_STATES.published) {
          navigate("/pro/home/experiences?tab=published")
        } else if (backRoute === EXPERIENCE_STATES.draft) {
          navigate("/pro/home/experiences?tab=draft")
        } else {
          navigate("/pro/home/experiences")
        }
      }
    }

    if (actionType === "duplicate") {
      executeAction(trip, actionType, action, afterAction)
      return
    }

    showConfirm(
      {
        title,
        message,
        highlightText: trip.title,
        confirmLabel,
        cancelLabel: t("global.cancel"),
        ...(warningText && { warningText }),
        loading
      },
      () => executeAction(trip, actionType, action, afterAction)
    )
  }

  const isDuplicateInProgress = loading && currentActionTypeRef.current === "duplicate"

  return {
    handleDelete: (trip, onSuccess) =>
      handleExperiencesAction(trip, "delete", deleteTrip, onSuccess),
    handleDuplicate: (trip, onSuccess) =>
      handleExperiencesAction(trip, "duplicate", duplicateTrip, onSuccess),
    handleDeactivate: (trip, onSuccess) =>
      handleExperiencesAction(
        trip,
        "deactivate",
        (id) => updateTrip(id, { trip: { status: "unpublished" } }),
        onSuccess
      ),
    handleShare: showShare,
    handlePublish: (trip, onSuccess) =>
      handleExperiencesAction(
        trip,
        "publish",
        (id) => updateTrip(id, { trip: { status: "published" } }),
        onSuccess
      ),
    isDuplicateInProgress,
    duplicatingTripId: isDuplicateInProgress ? currentTripIdRef.current : null,
    modal: (
      <>
        {confirmModal}
        {shareModal}
      </>
    )
  }
}
